import React, { useState } from "react";
import styled from "styled-components";
import { LuArrowUpRight } from "react-icons/lu";

import brk from "../../util/breakpoints";
import Container from "../Container";
import Reveal from "../Reveal";
import TypedWords from "../TypedWords";

const email = "james@thirdspear.dev";

// What the headline cycles through: "Have a strange little ___ idea?"
const ideaWords = ["game", "app", "tool", "product"];

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  background: var(--lime);
  overflow: hidden;
`;

const Orbit = styled.span`
  position: absolute;
  right: ${(props) => `${props.$right}px`};
  top: ${(props) => `${props.$top}px`};
  width: ${(props) => `${props.$size}px`};
  height: ${(props) => `${props.$size}px`};
  border-radius: 50%;
  border: 1px solid ${(props) => props.$color};
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 32px;
  min-height: 420px;
  padding: 76px 0;
  @media ${brk.device.pc} {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  @media ${brk.device.tablet} {
    min-height: 0;
    padding: 64px 0;
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
  max-width: 930px;
`;

const ContactMarker = styled.p`
  color: var(--ink);
  font-family: var(--fontMono);
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
`;

const Headline = styled.h2`
  color: var(--ink);
  font-family: var(--fontHeading);
  font-size: clamp(34px, 6vw, 64px);
  font-weight: 700;
  line-height: 0.98;
`;

const Accent = styled.span`
  color: #5c3fd8;
`;

const Description = styled.p`
  max-width: 660px;
  color: #27300f;
  font-family: var(--fontBody);
  font-size: 16px;
  line-height: 1.5;
`;

const Action = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 11px;
  flex-shrink: 0;
  p {
    color: #384714;
    font-family: var(--fontMono);
    font-size: 9px;
    white-space: nowrap;
  }
  @media ${brk.device.pc} {
    align-items: flex-start;
  }
`;

const EmailButton = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 58px;
  padding: 0 24px;
  border-radius: 8px;
  background: var(--ink);
  color: var(--text);
  font-family: var(--fontBody);
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  svg {
    color: var(--lime);
    transition: transform 0.2s;
  }
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(8, 10, 16, 0.3);
    svg {
      transform: translate(2px, -2px);
    }
  }
`;

function Contact() {
  const [copied, setCopied] = useState(false);

  // mailto does nothing on machines without a mail app set up, so the address is also copied.
  const copyEmail = () => {
    if (!navigator.clipboard) return;

    navigator.clipboard
      .writeText(email)
      .then(() => {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2500);
      })
      .catch(() => {});
  };

  return (
    <Wrapper id="contact">
      <Container>
        <Orbit $right={-100} $top={-180} $size={520} $color="rgba(8, 10, 16, 0.22)" />
        <Orbit $right={-10} $top={-90} $size={340} $color="rgba(8, 10, 16, 0.32)" />
        <Reveal>
          <Content>
            <Copy>
              <ContactMarker>Start a conversation</ContactMarker>
              <Headline>
                Have a strange little <TypedWords words={ideaWords} />
                <br />
                idea? <Accent>I’d like to hear it.</Accent>
              </Headline>
              <Description>
                Tell me what you’re building and where you’d like a hand. Short
                notes are perfect.
              </Description>
            </Copy>
            <Action>
              <EmailButton href={`mailto:${email}`} onClick={copyEmail}>
                {email}
                <LuArrowUpRight size={17} />
              </EmailButton>
              <p>{copied ? "ADDRESS COPIED TO CLIPBOARD" : "USUALLY REPLIES IN 2–3 DAYS"}</p>
            </Action>
          </Content>
        </Reveal>
      </Container>
    </Wrapper>
  );
}

export default Contact;
